/**
 * @fileoverview Container component for analytics dashboard metric cards display.
 * Renders a 3-card layout: Sales Total, Total Costs, and Profit.
 * @module shared/components/ui/MetricCards
 */

import { ReactElement, ReactNode } from 'react'
import { useTranslation } from 'react-i18next'
import { ResponsiveInfoPanel } from './ResponsiveInfoPanel'
import { useIsBelowDesktop } from '@/shared/hooks/useIsMobile'
import { SearchX } from 'lucide-react'
import { Card } from '@/components/ui/card'
import { ChartEmptyState } from './ChartEmptyState'
import {
  HoverInfoHeader,
  HoverInfoDivider,
  HoverProductRow,
} from './HoverInfoCard'
import { AnimatedValue } from './AnimatedValue'
import { useAggregatedProductMetrics } from '@/shared/hooks/dashboard/useAggregatedProductMetrics'
import { useAdPlatformBreakdown } from '@/shared/hooks/dashboard/useAdPlatformBreakdown'
import { useProfitByCountryData } from '@/shared/hooks/dashboard/useProfitByCountryData'
import { useLabelResolver } from '@/shared/hooks/useLabelResolver'
import { useProductGroupsMetadata } from '@/shared/hooks'
import { formatInt } from '@/shared/utils/metricsUtils'
import { useEcommerceTotals, useDashboardData } from '@/shared/hooks/dashboard'
import { useCompactMoney } from '@/shared/hooks/useCompactMoney'
import { useFilter } from '@/shared/hooks/useFilter'
import { cn } from '@/shared/utils/cn'
import { Skeleton } from './index'
import { Money } from './Money'
import { ShoppingCart, BanknoteArrowDown, CircleDollarSign, ChartPie } from 'lucide-react'
import { ExpandIcon } from './ExpandIcon'
import { AnimatedCard } from './AnimatedCard'
import { BackgroundGradientAnimation } from '@/components/ui/background-gradient-animation'
import { Link, useNavigate } from 'react-router-dom'
import { BsMeta } from 'react-icons/bs'
import { GoogleAdsIcon } from '@/shared/components/ui/icons/GoogleAdsIcon'
import { useIntegrationsStatus } from '@/features/settings/hooks/useIntegrationsStatus'
import { FacebookOAuthConnect } from '@/features/settings/components/FacebookOAuthConnect'
import { GoogleAdsOAuthConnect } from '@/features/settings/components/GoogleAdsOAuthConnect'
import { Button } from '@/components/ui/button'

/**
 * System variant SKU keys that should be excluded from money losers view.
 * 'general' (Store-Wide) is excluded because it always appears as a loss
 * without proper ad attribution tracking.
 */
const SYSTEM_VARIANT_SKUS_EXCLUDED_FROM_LOSERS = new Set(['general'])

/**
 * Gradient theme colors for the background animation on the Profit card.
 */
const GRADIENT_THEMES = {
  positive: {
    gradientBackgroundStart: 'rgb(2, 44, 34)', // emerald-950
    gradientBackgroundEnd: 'rgb(15, 23, 42)', // slate-900
    firstColor: '6, 78, 59', // emerald-800
    secondColor: '30, 41, 59', // slate-800
    thirdColor: '5, 150, 105', // emerald-600
    fourthColor: '15, 23, 42', // slate-900
    fifthColor: '2, 44, 34', // emerald-950
  },
  negative: {
    gradientBackgroundStart: 'rgb(40, 0, 10)', // deep rose/red
    gradientBackgroundEnd: 'rgb(20, 0, 5)', // almost black red
    firstColor: '120, 10, 40', // darker rose
    secondColor: '110, 15, 50', // darker pink
    thirdColor: '100, 10, 40', // darker pink/rose
    fourthColor: '80, 7, 36', // rose-950
    fifthColor: '60, 5, 20', // deep red
  },
}

/**
 * Checks if a product key represents a system variant that should be excluded from money losers.
 * Uses the dimension group's members array to find the underlying SKU key.
 */
function isExcludedSystemVariant(
  productKey: string,
  getGroup: (key: string) => { members?: string[] } | undefined
): boolean {
  const group = getGroup(productKey)
  if (!group?.members) return false
  return group.members.some(sku => SYSTEM_VARIANT_SKUS_EXCLUDED_FROM_LOSERS.has(sku))
}

/**
 * Props for the MetricCards component.
 */
interface MetricCardsProps {
  /** Additional CSS classes for customization */
  className?: string
}

/**
 * Component that handles loading state for metric cards.
 * Displays 3 skeleton cards matching the final 3-card layout.
 *
 * @component
 */
function MetricCardsLoadingState(): ReactElement {
  return (
    <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
      {[1, 2, 3].map(index => (
        <div key={index} className="glass-card flex flex-col">
          {/* Primary zone skeleton */}
          <div className="flex-1 px-4 py-5 flex flex-col items-center justify-center">
            <Skeleton className="h-9 w-28 mb-2" />
            <Skeleton className="h-4 w-20" />
          </div>
          {/* Secondary strip skeleton */}
          <div className="border-t border-border/30 bg-muted/20 px-4 py-3">
            <Skeleton className="h-3.5 w-32 mx-auto" />
          </div>
        </div>
      ))}
    </div>
  )
}

/**
 * Container component for displaying analytics dashboard metric cards.
 * Renders a 3-card layout: Sales Total, Total Costs, and Profit.
 * Uses centralized dashboard data provider for optimized data fetching.
 *
 * @component
 * @example
 * ```tsx
 * <MetricCards />
 * ```
 */
export function MetricCards({ className }: MetricCardsProps): ReactElement {
  const { t } = useTranslation('features-home')
  const navigate = useNavigate()
  // Use centralized dashboard data
  const { revenueCard, costCard, isLoading, error, data } = useEcommerceTotals()
  const { salesTimeQuery } = useDashboardData()
  const { products } = useAggregatedProductMetrics()
  const { data: platformData } = useAdPlatformBreakdown()
  const { data: profitByCountry } = useProfitByCountryData()
  const { productLabel } = useLabelResolver()
  const { getGroup } = useProductGroupsMetadata()
  const compactMoney = useCompactMoney()
  const { profitBasis } = useFilter()
  const integrationsQuery = useIntegrationsStatus()
  const isBelowDesktop = useIsBelowDesktop()

  // Hide business costs segment when viewing "Before business costs" (CM2) mode
  const showBusinessCosts = profitBasis !== 'CM2'

  // Extract indirect costs (business costs) data
  const indirectCosts = salesTimeQuery.data?.indirectCosts

  // Lightweight country flag resolver. Accepts ISO codes (PL) or common names.
  const flagForCountry = (raw: string | null | undefined): string => {
    if (!raw) return ''
    const s = String(raw).trim()
    const upper = s.toUpperCase()
    // If two-letter ISO code, compute regional indicator symbols
    if (/^[A-Z]{2}$/.test(upper)) {
      const codePoint = (ch: string): number => 0x1f1e6 + (ch.charCodeAt(0) - 65)
      return String.fromCodePoint(codePoint(upper.charAt(0)), codePoint(upper.charAt(1)))
    }
    // Common name fallbacks
    const nameMap: Record<string, string> = {
      POLAND: 'PL',
      'CZECH REPUBLIC': 'CZ',
      CZECH: 'CZ',
      SLOVAKIA: 'SK',
      IRELAND: 'IE',
      GERMANY: 'DE',
      FRANCE: 'FR',
      SPAIN: 'ES',
      ITALY: 'IT',
      'UNITED KINGDOM': 'GB',
      UK: 'GB',
      USA: 'US',
      'UNITED STATES': 'US',
    }
    const code = nameMap[upper]
    if (code) return flagForCountry(code)
    return ''
  }

  const imageFor = (sku: string): string | null | undefined => getGroup(sku)?.imageUrl ?? null

  const goToProduct = (sku: string): void => {
    const group = getGroup(sku)
    navigate(`/dashboard/product/${encodeURIComponent(sku)}`, {
      state: {
        memberSkus: group?.members ?? [],
        productName: productLabel(sku),
        imageUrl: group?.imageUrl ?? null,
      },
    })
  }

  if (isLoading) {
    return <MetricCardsLoadingState />
  }

  if (error) {
    return <div className="p-6 text-center text-destructive">{error}</div>
  }

  // Show empty state if no data available
  if (!revenueCard || !costCard) {
    return (
      <Card className="rounded-xl border border-border/40 bg-card/30">
        <ChartEmptyState
          variant="banner"
          icon={SearchX}
          message={t('metrics.noData')}
          hint={t('charts.emptyHint')}
        />
      </Card>
    )
  }

  // Extract raw numeric values for animation
  const revenueRaw = data?.revenue ?? 0
  const profitRaw = data?.profit ?? null
  const adSpendRaw = data?.adSpend ?? 0
  const cogsRaw = data?.cogs ?? 0
  const ordersRaw = data?.orders ?? 0
  const marginRaw = data?.margin ?? 0
  const businessCostsRaw = indirectCosts?.total ?? 0

  // Compute total costs: Ad Spend + COGS + Business Costs
  const totalCostsRaw = adSpendRaw + cogsRaw + businessCostsRaw

  // Compute average order value
  const avgOrderValue = ordersRaw > 0 ? revenueRaw / ordersRaw : 0

  // Top products for hover panels
  const topBy = {
    sales: [...products].sort((a, b) => b.sales - a.sales).slice(0, 4),
    adSpend: [...products].sort((a, b) => b.adSpend - a.adSpend).slice(0, 4),
    cogs: [...products].sort((a, b) => b.cogs - a.cogs).slice(0, 4),
    profitGain: [...products]
      .filter(p => p.profit > 0)
      .sort((a, b) => b.profit - a.profit)
      .slice(0, 4),
    profitLoss: [...products]
      .filter(p => p.profit < 0 && !isExcludedSystemVariant(p.sku, getGroup))
      .sort((a, b) => a.profit - b.profit)
      .slice(0, 4),
  }

  // Determine profit styling
  const isProfitPositive = profitRaw !== null && profitRaw >= 0
  const profitDisplayValue = profitRaw !== null ? Math.abs(profitRaw) : 0
  const profitPrefix = profitRaw !== null ? (profitRaw >= 0 ? '+' : '-') : ''

  // ============================================
  // HOVER PANEL CONTENT RENDERERS
  // ============================================

  /** Builds a preset link to the performance-by-product table page */
  const perfLink = (columns: string[], sort: string): string =>
    `/dashboard/performance?columns=${columns.join(',')}&sort=${sort}`

  /** Expand icon for clickable hover panel headers (header itself carries the link) */
  const expandIcon: ReactNode = <ExpandIcon />

  /** Fully clickable panel header strip — navigates to performance table with preset */
  const headerLink = (label: string, href: string, labelClassName: string, className: string): ReactNode => (
    <Link
      to={href}
      className={cn(
        'group flex items-center justify-between transition-colors duration-200 hover:bg-white/5',
        className
      )}
    >
      <p className={labelClassName}>{label}</p>
      {expandIcon}
    </Link>
  )

  /** Sales hover panel content */
  const renderSalesHoverContent = (): ReactNode => (
    <div>
      <HoverInfoHeader
        title={t('metricLabels.mostRevenue', { defaultValue: 'Most revenue' })}
        leading={
          <div
            className="h-2.5 w-2.5 rounded-full"
            style={{ backgroundColor: 'var(--color-chart-legend-revenue)' }}
            aria-hidden="true"
          />
        }
        leadingClassName="self-center"
        trailing={expandIcon}
        href={perfLink(['sales', 'adSpend', 'roas'], 'sales:desc')}
      />
      <HoverInfoDivider />
      <div>
        {topBy.sales.map(p => (
          <HoverProductRow
            key={`rev-${p.sku}`}
            imageUrl={imageFor(p.sku)}
            title={`${formatInt(p.quantity)} × ${productLabel(p.sku)}`}
            value={compactMoney(Math.round(p.sales))}
            leftMeta={`${compactMoney(Math.round(p.adSpend))} ${t('metricLabels.onAds', { defaultValue: 'ad spent' })}`}
            dimQuantityPrefix
            onClick={() => goToProduct(p.sku)}
          />
        ))}
      </div>
      {profitByCountry.length > 0 && ((): ReactNode => {
        const sorted = profitByCountry.slice().sort((a, b) => b.revenue - a.revenue)
        const top2 = sorted.slice(0, 2)
        const restTotal = sorted.slice(2).reduce((sum, c) => sum + c.revenue, 0)
        return (
          <div className="flex items-center gap-5 px-6 py-3 flex-wrap border-t border-border/30">
            {top2.map(c => (
              <span key={c.country} className="inline-flex items-center gap-1 text-sm text-muted-foreground whitespace-nowrap">
                <span>{flagForCountry(c.country)}</span>
                <span className="font-medium text-foreground/80">{compactMoney(Math.round(c.revenue))}</span>
              </span>
            ))}
            {restTotal > 0 && (
              <span className="inline-flex items-center gap-1 text-sm text-muted-foreground whitespace-nowrap">
                <span>🌍</span>
                <span className="font-medium text-foreground/80">{compactMoney(Math.round(restTotal))}</span>
              </span>
            )}
          </div>
        )
      })()}
    </div>
  )

  /** Ad Spend hover panel content */
  const renderAdSpendHoverContent = (): ReactNode => {
    const isMeta = (n: string): boolean => /meta|facebook|instagram/i.test(n)
    const isGoogle = (n: string): boolean => /google/i.test(n)
    const getIcon = (name: string): ReactNode =>
      isMeta(name)
        ? <BsMeta className="h-3 w-3" />
        : isGoogle(name)
          ? <GoogleAdsIcon className="h-3 w-3" />
          : null

    // Check if ad integrations are connected
    const integrations = integrationsQuery.data ?? []
    const facebookStatus = integrations.find(i => i.provider === 'FACEBOOK')
    const googleStatus = integrations.find(i => i.provider === 'GOOGLE_ADS')
    const hasNoAdIntegrations =
      (!facebookStatus || facebookStatus.status === 'NOT_CONFIGURED') &&
      (!googleStatus || googleStatus.status === 'NOT_CONFIGURED')

    // Check if there's any actual ad spend
    const hasNoAdSpend = adSpendRaw === 0

    // Show connect buttons when no integrations and no ad spend
    if (hasNoAdIntegrations && hasNoAdSpend) {
      return (
        <div className="pb-3">
          <HoverInfoHeader
            title={t('metricLabels.adSpend')}
            leading={
              <div
                className="h-2.5 w-2.5 rounded-full"
                style={{ backgroundColor: 'var(--chart-legend-adspend)' }}
                aria-hidden="true"
              />
            }
            leadingClassName="self-center"
          />
          <HoverInfoDivider />
          <div className="flex flex-col items-center justify-center py-6 gap-4">
            <p className="text-sm text-muted-foreground text-center">
              {t('charts.adPlatformSpend.connectPrompt', {
                defaultValue: 'Connect your ad platforms to see spend data',
              })}
            </p>
            <div className="flex flex-col items-stretch gap-2 w-full">
              <FacebookOAuthConnect
                buttonLabel={t('charts.adPlatformSpend.connectFacebook', {
                  defaultValue: 'Connect Facebook Ads',
                })}
                buttonIcon={<img src="/meta-favicon.png" alt="" className="size-4" />}
              />
              <GoogleAdsOAuthConnect
                buttonLabel={t('charts.adPlatformSpend.connectGoogle', {
                  defaultValue: 'Connect Google Ads',
                })}
                buttonIcon={<img src="/google-favicon.png" alt="" className="size-4" />}
              />
            </div>
          </div>
        </div>
      )
    }

    return (
      <div>
        <HoverInfoHeader
          title={t('metricLabels.adSpend')}
          leading={
            <div
              className="h-2.5 w-2.5 rounded-full"
              style={{ backgroundColor: 'var(--chart-legend-adspend)' }}
              aria-hidden="true"
            />
          }
          leadingClassName="self-center"
          trailing={expandIcon}
          href={perfLink(['adSpend', 'googleAds', 'metaAds', 'roas'], 'adSpend:desc')}
        />
        <HoverInfoDivider />
        <div>
          {topBy.adSpend.map(p => {
            const topPlatforms = Object.entries(p.adPlatformBreakdown)
              .sort((a, b) => b[1] - a[1])
              .slice(0, 2)
            const left = topPlatforms[0]
            const right = topPlatforms[1]
            return (
              <HoverProductRow
                key={`ad-${p.sku}`}
                imageUrl={imageFor(p.sku)}
                title={productLabel(p.sku)}
                value={compactMoney(Math.round(p.adSpend))}
                leftMetaLabel={t('metricLabels.topPlatform', { defaultValue: 'Top platform' })}
                leftMeta={
                  left ? (
                    <span className="flex items-center gap-1">
                      {getIcon(left[0])}
                      {compactMoney(Math.round(left[1]))}
                    </span>
                  ) : undefined
                }
                rightMetaLabel={t('metricLabels.secondPlatform', { defaultValue: 'Second platform' })}
                rightMeta={
                  right ? (
                    <span className="flex items-center gap-1">
                      {getIcon(right[0])}
                      {compactMoney(Math.round(right[1]))}
                    </span>
                  ) : undefined
                }
                onClick={() => goToProduct(p.sku)}
              />
            )
          })}
        </div>
        {((): ReactNode => {
          const meta = platformData.find(p => isMeta(p.platform))
          const google = platformData.find(p => isGoogle(p.platform))
          let rows = [] as { platform: string; amount: number; icon: ReactNode }[]
          if (meta)
            rows.push({
              platform: 'Meta',
              amount: meta.amount,
              icon: <BsMeta className="h-3.5 w-3.5" />,
            })
          if (google)
            rows.push({
              platform: 'Google',
              amount: google.amount,
              icon: <GoogleAdsIcon className="h-3.5 w-3.5" />,
            })
          if (rows.length === 0) {
            rows = platformData.slice(0, 2).map(p => ({
              platform: p.platform,
              amount: p.amount,
              icon: getIcon(p.platform),
            }))
          }
          if (rows.length === 0) return null
          return (
            <div className="flex items-center gap-5 px-6 py-3 flex-wrap border-t border-border/30">
              {rows.map(r => (
                <span key={r.platform} className="inline-flex items-center gap-1.5 text-sm text-muted-foreground whitespace-nowrap">
                  {r.icon}
                  <span className="font-medium text-foreground/80">{compactMoney(Math.round(r.amount))}</span>
                </span>
              ))}
            </div>
          )
        })()}
      </div>
    )
  }

  /** COGS hover panel content */
  const renderCogsHoverContent = (): ReactNode => (
    <div className="pb-3">
      <HoverInfoHeader
        title={t('metricLabels.cogs')}
        leading={
          <div
            className="h-2.5 w-2.5 rounded-full"
            style={{ backgroundColor: 'var(--chart-legend-cogs)' }}
            aria-hidden="true"
          />
        }
        leadingClassName="self-center"
        trailing={expandIcon}
        href={perfLink(['cogs', 'sales', 'profit'], 'cogs:desc')}
      />
      <HoverInfoDivider />
      <div>
        {topBy.cogs.map(p => (
          <HoverProductRow
            key={`cogs-${p.sku}`}
            imageUrl={imageFor(p.sku)}
            title={`${formatInt(p.quantity)} × ${productLabel(p.sku)}`}
            value={compactMoney(Math.round(p.cogs))}
            leftMeta={
              p.quantity > 0
                ? `${compactMoney(Math.round(p.cogs / p.quantity))} ${t('metricLabels.costPerUnit', { defaultValue: 'cost per unit' })}`
                : undefined
            }
            dimQuantityPrefix
            onClick={() => goToProduct(p.sku)}
          />
        ))}
      </div>
    </div>
  )

  /** Business Costs hover panel content */
  const renderBusinessCostsHoverContent = (): ReactNode => {
    const hasNoBusinessCosts = !indirectCosts?.groups.length && (indirectCosts?.total ?? 0) === 0

    if (hasNoBusinessCosts) {
      return (
        <div>
          <HoverInfoHeader
            title={t('businessCosts.title', { defaultValue: 'Business Costs' })}
            leading={
              <div
                className="h-2.5 w-2.5 rounded-full"
                style={{ backgroundColor: 'var(--chart-legend-overhead)' }}
                aria-hidden="true"
              />
            }
            leadingClassName="self-center"
          />
          <HoverInfoDivider />
          <div className="flex flex-col items-center justify-center py-6 gap-4">
            <p className="text-sm text-muted-foreground text-center">
              {t('businessCosts.emptyPrompt', {
                defaultValue: 'Track your operating expenses to see the full picture',
              })}
            </p>
            <Link to="/indirect-costs?view=indirect">
              <Button variant="outline" size="sm" className="gap-2">
                <ChartPie className="size-4" />
                {t('businessCosts.addButton', {
                  defaultValue: 'Add Business Costs',
                })}
              </Button>
            </Link>
          </div>
        </div>
      )
    }

    return (
      <div>
        <HoverInfoHeader
          title={t('businessCosts.title', { defaultValue: 'Business Costs' })}
          leading={
            <div
              className="h-2.5 w-2.5 rounded-full"
              style={{ backgroundColor: 'var(--chart-legend-overhead)' }}
              aria-hidden="true"
            />
          }
          leadingClassName="self-center"
        />
        <HoverInfoDivider />
        <div>
          {indirectCosts?.groups.slice(0, 8).map((group, idx) => {
            const displayName =
              (group.subcategoryDisplayName && group.subcategoryDisplayName.trim()) ||
              (group.label && group.label.trim()) ||
              t('businessCosts.unknown', { defaultValue: 'Unnamed subcategory' })
            return (
              <div
                key={`cost-${idx}`}
                className="flex items-center justify-between gap-4 px-6 py-3 border-b border-border/30 last:border-b-0"
              >
                <div className="flex items-center gap-2 min-w-0">
                  <span className="truncate text-sm text-foreground/70">{displayName}</span>
                </div>
                <div className="shrink-0 font-mono text-sm font-medium">
                  <Money value={group.total} minFraction={0} maxFraction={0} />
                </div>
              </div>
            )
          })}
          {(indirectCosts?.otherTotal ?? 0) > 0 && (indirectCosts?.groups.length ?? 0) < 8 && (
            <div className="flex items-center justify-between gap-4 px-6 py-2">
              <div className="flex items-center gap-2 min-w-0">
                <span className="truncate text-sm text-muted-foreground">
                  {t('businessCosts.others', { defaultValue: 'Others' })}
                </span>
              </div>
              <div className="shrink-0 font-mono text-sm font-medium">
                <Money value={indirectCosts?.otherTotal ?? 0} minFraction={0} maxFraction={0} />
              </div>
            </div>
          )}
        </div>
        <div className="flex items-center justify-between px-6 pt-2 pb-3 bg-black/10 border-t border-border">
          <div className="flex items-center gap-2">
            <div
              className="h-2.5 w-2.5 rounded-full shrink-0"
              style={{ backgroundColor: 'var(--chart-legend-overhead)' }}
            />
            <span className="text-sm text-foreground/80">
              {t('businessCosts.totalShort', { defaultValue: 'Total' })}
            </span>
          </div>
          <span className="font-mono text-sm font-semibold text-foreground/70">
            <Money value={indirectCosts?.total ?? 0} minFraction={0} maxFraction={0} />
          </span>
        </div>
      </div>
    )
  }

  /** Profit hover panel content - stacks vertically on mobile/tablet, 2-col on desktop */
  const renderProfitHoverContent = (): ReactNode => (
    <div className="pb-3">
      {/* On mobile/tablet (sheet): stacked layout. On desktop (popover): 2-column */}
      {isBelowDesktop ? (
        <>
          {/* Most profitable section */}
          {headerLink(
            t('metricLabels.mostProfitable', { defaultValue: 'Most profitable:' }),
            perfLink(['sales', 'totalExpenses', 'profit', 'margin'], 'profit:desc'),
            'text-lg font-semibold',
            'bg-black/30 border-b border-border px-4 py-3'
          )}
          <div className="border-b border-border/50">
            {topBy.profitGain.map(p => (
              <HoverProductRow
                key={`gain-${p.sku}`}
                imageUrl={imageFor(p.sku)}
                title={`${formatInt(p.quantity)} × ${productLabel(p.sku)}`}
                value={`+${compactMoney(Math.round(p.profit))}`}
                valueClassName="text-[var(--color-positive)]"
                leftMeta={`${compactMoney(Math.round(p.adSpend))} ${t('metricLabels.onAds', { defaultValue: 'ad spent' })}`}
                dimQuantityPrefix
                onClick={() => goToProduct(p.sku)}
              />
            ))}
          </div>
          {/* Money losers section */}
          {headerLink(
            t('metricLabels.moneyLosers', { defaultValue: 'Money losers:' }),
            perfLink(['sales', 'totalExpenses', 'profit', 'margin'], 'profit:asc'),
            'text-lg font-semibold',
            'bg-black/30 border-b border-border px-4 py-3'
          )}
          <div>
            {topBy.profitLoss.map(p => (
              <HoverProductRow
                key={`loss-${p.sku}`}
                imageUrl={imageFor(p.sku)}
                title={`${formatInt(p.quantity)} × ${productLabel(p.sku)}`}
                value={compactMoney(Math.round(p.profit))}
                valueClassName="text-[var(--color-negative)]"
                leftMeta={`${compactMoney(Math.round(p.adSpend))} ${t('metricLabels.onAds', { defaultValue: 'ad spent' })}`}
                dimQuantityPrefix
                onClick={() => goToProduct(p.sku)}
              />
            ))}
          </div>
        </>
      ) : (
        <>
          {/* Desktop: 2-column layout */}
          <div className="grid grid-cols-2 bg-black/30 border-b border-border">
            {headerLink(
              t('metricLabels.mostProfitable', { defaultValue: 'Most profitable:' }),
              perfLink(['sales', 'totalExpenses', 'profit', 'margin'], 'profit:desc'),
              'text-xl font-semibold',
              'py-4 px-6'
            )}
            {headerLink(
              t('metricLabels.moneyLosers', { defaultValue: 'Money losers:' }),
              perfLink(['sales', 'totalExpenses', 'profit', 'margin'], 'profit:asc'),
              'text-xl font-semibold',
              'py-4 px-6'
            )}
          </div>
          <div className="grid grid-cols-2">
            <div>
              {topBy.profitGain.map(p => (
                <HoverProductRow
                  key={`gain-${p.sku}`}
                  imageUrl={imageFor(p.sku)}
                  title={`${formatInt(p.quantity)} × ${productLabel(p.sku)}`}
                  value={`+${compactMoney(Math.round(p.profit))}`}
                  valueClassName="text-[var(--color-positive)]"
                  leftMeta={`${compactMoney(Math.round(p.adSpend))} ${t('metricLabels.onAds', { defaultValue: 'ad spent' })}`}
                  dimQuantityPrefix
                  onClick={() => goToProduct(p.sku)}
                />
              ))}
            </div>
            <div>
              {topBy.profitLoss.map(p => (
                <HoverProductRow
                  key={`loss-${p.sku}`}
                  imageUrl={imageFor(p.sku)}
                  title={`${formatInt(p.quantity)} × ${productLabel(p.sku)}`}
                  value={compactMoney(Math.round(p.profit))}
                  valueClassName="text-[var(--color-negative)]"
                  leftMeta={`${compactMoney(Math.round(p.adSpend))} ${t('metricLabels.onAds', { defaultValue: 'ad spent' })}`}
                  dimQuantityPrefix
                  onClick={() => goToProduct(p.sku)}
                />
              ))}
            </div>
          </div>
        </>
      )}
    </div>
  )

  // ============================================
  // 3-CARD LAYOUT RENDER
  // ============================================

  return (
    <div className={className}>
      <div className="grid grid-cols-1 md:grid-cols-3 gap-4 items-stretch">
        {/* ============================================ */}
        {/* CARD 1: SALES TOTAL */}
        {/* ============================================ */}
        <div className="h-full">
          <ResponsiveInfoPanel
            trigger={
              <AnimatedCard variant="subtle" className="h-full">
                <div className="glass-card !p-0 cursor-pointer flex flex-col h-full">
                  {/* Primary zone */}
                  <div className="flex-1 flex flex-col items-center justify-center px-4 py-10 bg-black/20">
                    <div className="text-3xl md:text-4xl font-semibold font-mono tracking-tight animated-card-value">
                      <AnimatedValue
                        value={revenueRaw}
                        formatter={compactMoney}
                        easing="easeOut"
                        duration={200}
                      />
                    </div>
                    <div className="flex items-center gap-2 mt-1.5 text-muted-foreground">
                      <ShoppingCart className="h-5 w-5" />
                      <span className="text-sm font-medium">{t('metrics.salesTotal')}</span>
                    </div>
                  </div>
                  {/* Secondary strip */}
                  <div className="border-t border-border/30 px-4 py-2.5 flex items-center justify-center min-h-[53px] hover:bg-black/40 transition-colors">
                    <p className="text-xs text-muted-foreground text-center">
                      {formatInt(ordersRaw)} {t('metrics.orders', { defaultValue: 'orders' })} · {compactMoney(Math.round(avgOrderValue))} {t('metrics.avg', { defaultValue: 'avg' })}
                    </p>
                  </div>
                </div>
              </AnimatedCard>
            }
            title={t('panelTitles.salesDetails')}
            align="center"
            side="top"
            size="one"
          >
            {renderSalesHoverContent()}
          </ResponsiveInfoPanel>
        </div>

        {/* ============================================ */}
        {/* CARD 2: TOTAL COSTS */}
        {/* ============================================ */}
        <AnimatedCard variant="subtle" className="h-full">
          <div className="glass-card !p-0 flex flex-col h-full">
            {/* Primary zone - triggers ad spend panel */}
            <ResponsiveInfoPanel
              trigger={
                <div className="flex-1 flex flex-col items-center justify-center px-4 py-10 bg-black/20 cursor-pointer hover:bg-black/30 transition-colors">
                  <div className="text-3xl md:text-4xl font-semibold font-mono tracking-tight animated-card-value">
                    <AnimatedValue
                      value={totalCostsRaw}
                      formatter={compactMoney}
                      easing="easeOut"
                      duration={200}
                    />
                  </div>
                  <div className="flex items-center gap-2 mt-1.5 text-muted-foreground">
                    <BanknoteArrowDown className="h-5 w-5" />
                    <span className="text-sm font-medium">
                      {showBusinessCosts
                        ? t('metrics.totalCosts', { defaultValue: 'Total Costs' })
                        : t('metrics.adsPlusCogs', { defaultValue: 'Ads + COGS' })}
                    </span>
                  </div>
                </div>
              }
              title={t('panelTitles.adSpendDetails')}
              align="center"
              side="top"
              size="one"
            >
              {renderAdSpendHoverContent()}
            </ResponsiveInfoPanel>
            {/* Secondary strip: cost segments with independent panel triggers */}
            <div className={cn(
              "border-t border-border/30 grid divide-x divide-border/30",
              showBusinessCosts ? "grid-cols-3" : "grid-cols-2"
            )}>
            {/* Ad Spend segment */}
            <ResponsiveInfoPanel
              trigger={
                <div className="flex flex-col items-center justify-center py-2.5 px-2 cursor-pointer hover:bg-black/40 transition-colors">
                  <span className="text-[10px] text-muted-foreground uppercase tracking-wide">
                    {t('metrics.adSpendShort', { defaultValue: 'Ad Spend' })}
                  </span>
                  <span className="text-xs font-medium font-mono mt-0.5">
                    {compactMoney(Math.round(adSpendRaw))}
                  </span>
                </div>
              }
              title={t('panelTitles.adSpendDetails')}
              align="center"
              side="bottom"
              size="one"
            >
              {renderAdSpendHoverContent()}
            </ResponsiveInfoPanel>

            {/* COGS segment */}
            <ResponsiveInfoPanel
              trigger={
                <div className="flex flex-col items-center justify-center py-2.5 px-2 cursor-pointer hover:bg-black/40 transition-colors">
                  <span className="text-[10px] text-muted-foreground uppercase tracking-wide">
                    {t('metrics.cogsShort', { defaultValue: 'COGS' })}
                  </span>
                  <span className="text-xs font-medium font-mono mt-0.5">
                    {compactMoney(Math.round(cogsRaw))}
                  </span>
                </div>
              }
              title={t('panelTitles.cogsDetails')}
              align="center"
              side="bottom"
              size="one"
            >
              {renderCogsHoverContent()}
            </ResponsiveInfoPanel>

            {/* Business Costs segment (hidden in CM2/before business costs view) */}
            {showBusinessCosts && (
              <ResponsiveInfoPanel
                trigger={
                  <div className="flex flex-col items-center justify-center py-2.5 px-2 cursor-pointer hover:bg-black/40 transition-colors">
                    <span className="text-[10px] text-muted-foreground uppercase tracking-wide">
                      {t('metrics.bizCostsShort', { defaultValue: 'Biz Costs' })}
                    </span>
                    <span className="text-xs font-medium font-mono mt-0.5">
                      {compactMoney(Math.round(businessCostsRaw))}
                    </span>
                  </div>
                }
                title={t('panelTitles.businessCosts')}
                align="center"
                side="bottom"
                size="one"
              >
                {renderBusinessCostsHoverContent()}
              </ResponsiveInfoPanel>
            )}
          </div>
        </div>
        </AnimatedCard>

        {/* ============================================ */}
        {/* CARD 3: PROFIT */}
        {/* ============================================ */}
        <div className="h-full">
          <ResponsiveInfoPanel
            trigger={
              <AnimatedCard variant="subtle" className="h-full">
                <div className="glass-card !p-0 cursor-pointer flex flex-col h-full relative overflow-hidden">
                  {/* Background gradient animation for profit card */}
                  <BackgroundGradientAnimation
                    {...GRADIENT_THEMES[isProfitPositive ? 'positive' : 'negative']}
                    containerClassName="absolute inset-0 opacity-50 pointer-events-none"
                    interactive={false}
                    size="120%"
                  />
                  {/* Primary zone */}
                  <div className="flex-1 flex flex-col items-center justify-center px-4 py-10 bg-black/20 relative z-10">
                    <div className={cn(
                      'text-3xl md:text-4xl font-semibold font-mono tracking-tight animated-card-value',
                      isProfitPositive ? 'text-positive' : 'text-negative'
                    )}>
                      <AnimatedValue
                        value={profitDisplayValue}
                        formatter={compactMoney}
                        prefix={profitPrefix}
                        easing="easeOut"
                        duration={200}
                        shimmer
                        shimmerColor={isProfitPositive ? 'positive' : 'negative'}
                      />
                    </div>
                    <div className="flex items-center gap-2 mt-1.5 text-muted-foreground">
                      <CircleDollarSign className="h-5 w-5" />
                      <span className="text-sm font-medium">{t('metrics.profit')}</span>
                    </div>
                  </div>
                  {/* Secondary strip */}
                  <div className="border-t border-border/30 px-4 py-2.5 relative z-10 flex items-center justify-center min-h-[53px] hover:bg-black/40 transition-colors">
                    <p className="text-xs text-muted-foreground text-center">
                      {marginRaw >= 0 ? '+' : ''}{marginRaw.toFixed(1)}% {t('metrics.margin', { defaultValue: 'Margin' })}
                    </p>
                  </div>
                </div>
              </AnimatedCard>
            }
            title={t('panelTitles.profitDetails')}
            align="center"
            side="top"
            size="two"
          >
            {renderProfitHoverContent()}
          </ResponsiveInfoPanel>
        </div>
      </div>
    </div>
  )
}
